import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import { THEME_PALETTES, buildTheme } from './themes'

const THEME_NAMES = Object.keys(THEME_PALETTES)
const FONT_SIZES = ['Small', 'Medium', 'Large']
const ROW_DENSITIES = ['Comfortable', 'Compact']

export default function Settings({ session, theme, applyThemeSettings, toggleTheme, subscriptionStatus, trialEndsAt, isBetaUser, isFounder, stripeCustomerId, onShowWelcome }) {
  const [selectedTheme, setSelectedTheme] = useState(theme.themeName || 'Ravenwood Classic')
  const [fontSize, setFontSize] = useState('Medium')
  const [rowDensity, setRowDensity] = useState('Comfortable')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (session?.user?.id) fetchSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  useEffect(() => {
    if (theme.themeName) setSelectedTheme(theme.themeName)
  }, [theme.themeName])

  const fetchSettings = async () => {
    const { data } = await supabase
      .from('settings')
      .select('theme_name, font_size, row_density')
      .eq('user_id', session.user.id)
      .single()
    if (data) {
      if (data.theme_name) setSelectedTheme(data.theme_name)
      if (data.font_size) setFontSize(data.font_size)
      if (data.row_density) setRowDensity(data.row_density)
    }
  }

  const handleThemeSelect = (name) => {
    setSelectedTheme(name)
    applyThemeSettings(name, theme.mode)
  }

  const handleSave = async () => {
    setSaving(true)
    await supabase.from('settings').upsert({
      user_id: session.user.id,
      theme_name: selectedTheme,
      font_size: fontSize,
      row_density: rowDensity,
    }, { onConflict: 'user_id' })
    applyThemeSettings(selectedTheme, theme.mode, fontSize, rowDensity)
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const label = (text) => (
    <div style={{
      fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em',
      textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif",
      marginBottom: 14,
    }}>{text}</div>
  )

  const sectionCard = (children) => (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      padding: '28px 32px', marginBottom: 20,
    }}>
      {children}
    </div>
  )

  const pillBtn = (value, current, onClick) => {
    const isActive = value === current
    return (
      <button
        key={value}
        onClick={() => onClick(value)}
        style={{
          padding: '10px 28px',
          background: isActive ? theme.accent : theme.surfaceAlt,
          color: isActive ? (theme.mode === 'dark' ? '#000' : '#fff') : theme.textSecondary,
          border: `1px solid ${isActive ? theme.accent : theme.border}`,
          borderRadius: 4, cursor: 'pointer', fontSize: 13,
          fontFamily: "'Nunito Sans', sans-serif",
          transition: 'all 0.15s',
        }}
      >
        {value}
      </button>
    )
  }

  return (
    <div style={{ maxWidth: 760 }}>

      {/* Theme picker */}
      {sectionCard(
        <>
          {label('Theme')}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {THEME_NAMES.map(name => {
              const preview = buildTheme(name, 'dark')
              const isSelected = selectedTheme === name
              return (
                <div
                  key={name}
                  onClick={() => handleThemeSelect(name)}
                  style={{
                    cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
                    border: isSelected ? `2px solid ${preview.accent}` : `2px solid ${theme.border}`,
                    transition: 'all 0.15s',
                    boxShadow: isSelected ? `0 0 16px ${preview.accent}40` : 'none',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = preview.accent + '80' }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = theme.border }}
                >
                  {/* Mini preview */}
                  <div style={{ background: preview.bg, padding: 10 }}>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      <div style={{ width: 20, background: preview.surface, borderRadius: 3, padding: '4px 3px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {[1,2,3].map(i => (
                          <div key={i} style={{ height: 2, borderRadius: 1, background: i === 1 ? preview.accent : preview.border }} />
                        ))}
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', gap: 3 }}>
                          {[1,2,3].map(i => (
                            <div key={i} style={{ flex: 1, background: preview.surface, borderRadius: 3, padding: 4, border: `1px solid ${preview.border}` }}>
                              <div style={{ height: 2, background: i === 1 ? preview.accent : preview.text, borderRadius: 1, opacity: i === 1 ? 1 : 0.6 }} />
                            </div>
                          ))}
                        </div>
                        {[1,2].map(i => (
                          <div key={i} style={{ background: preview.surface, borderRadius: 2, padding: '3px 5px', border: `1px solid ${preview.border}`, display: 'flex', gap: 4, alignItems: 'center' }}>
                            <div style={{ flex: 2, height: 2, background: preview.text, borderRadius: 1, opacity: 0.7 }} />
                            <div style={{ width: 5, height: 5, borderRadius: '50%', background: i === 1 ? preview.accent : preview.textMuted }} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {[preview.bg, preview.surface, preview.accent, preview.text, preview.textSecondary].map((c, i) => (
                        <div key={i} style={{ flex: 1, height: 10, borderRadius: 2, background: c, border: '1px solid rgba(255,255,255,0.05)' }} />
                      ))}
                    </div>
                  </div>
                  <div style={{
                    background: preview.surface, borderTop: `1px solid ${preview.border}`,
                    padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ color: preview.text, fontSize: 12, fontFamily: "'Nunito Sans', sans-serif" }}>{name}</span>
                    <div style={{
                      width: 12, height: 12, borderRadius: '50%',
                      border: `2px solid ${preview.accent}`,
                      background: isSelected ? preview.accent : 'transparent',
                    }} />
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Light / Dark mode */}
      {sectionCard(
        <>
          {label('Mode')}
          <div style={{ display: 'flex', gap: 12 }}>
            {pillBtn('☀️  Light', theme.mode === 'light' ? '☀️  Light' : '', () => { if (theme.mode === 'dark') toggleTheme() })}
            {pillBtn('🌙  Dark', theme.mode === 'dark' ? '🌙  Dark' : '', () => { if (theme.mode === 'light') toggleTheme() })}
          </div>
        </>
      )}

      {/* Font size */}
      {sectionCard(
        <>
          {label('Font Size')}
          <div style={{ display: 'flex', gap: 12 }}>
            {FONT_SIZES.map(size => pillBtn(size, fontSize, setFontSize))}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 10, fontFamily: "'Nunito Sans', sans-serif" }}>
            Font size preferences will apply across all panels in a future update.
          </div>
        </>
      )}

      {/* Row density */}
      {sectionCard(
        <>
          {label('Table Row Density')}
          <div style={{ display: 'flex', gap: 12 }}>
            {ROW_DENSITIES.map(d => pillBtn(d, rowDensity, setRowDensity))}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 10, fontFamily: "'Nunito Sans', sans-serif" }}>
            Row density will apply to Projects, Pitches, and Income tables in a future update.
          </div>
        </>
      )}

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: theme.accent, color: theme.mode === 'dark' ? '#000' : '#fff',
            border: 'none', padding: '12px 36px', cursor: saving ? 'default' : 'pointer',
            fontSize: 13, fontFamily: "'Open Sans', sans-serif",
            letterSpacing: '0.08em', textTransform: 'uppercase',
            opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s',
          }}
        >
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        {saved && (
          <div style={{ fontSize: 12, color: theme.accent, fontFamily: "'Nunito Sans', sans-serif" }}>
            ✓ Saved
          </div>
        )}
      </div>

      {/* Subscription */}
      {sectionCard(
        <>
          {label('Subscription')}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ fontSize: 15, color: theme.text, fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}>
              {isBetaUser
                ? 'Beta Access — no billing required'
                : subscriptionStatus === 'paid'
                  ? 'Lifetime Access'
                  : subscriptionStatus === 'active'
                    ? 'Lifetime Access'
                    : subscriptionStatus === 'trialing' && trialEndsAt
                      ? `Free Trial — ${Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))} days remaining`
                      : subscriptionStatus === 'canceled'
                        ? 'Access Ended'
                        : subscriptionStatus === 'past_due'
                          ? 'Payment Past Due'
                          : 'No active access'}
            </div>
            {isFounder && (
              <div style={{
                background: '#d9ad2b', color: '#0a2342', borderRadius: 999,
                padding: '2px 10px', fontSize: 11, fontFamily: "'Open Sans', sans-serif",
                fontWeight: 700, letterSpacing: '0.06em',
              }}>
                Founder
              </div>
            )}
          </div>
          {stripeCustomerId && subscriptionStatus !== 'paid' && subscriptionStatus !== 'active' && (
            <button
              onClick={async () => {
                const res = await fetch('/api/create-portal-session', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ stripeCustomerId }),
                })
                const { url } = await res.json()
                if (url) window.location.href = url
              }}
              style={{
                background: 'transparent', border: `1px solid ${theme.border}`,
                color: theme.textSecondary, cursor: 'pointer', fontSize: 12,
                padding: '10px 24px', fontFamily: "'Open Sans', sans-serif",
                letterSpacing: '0.08em', textTransform: 'uppercase',
              }}
            >
              View Billing History
            </button>
          )}
        </>
      )}

      {/* Help */}
      <div style={{ marginTop: 32, paddingTop: 20, borderTop: `1px solid ${theme.border}` }}>
        <div style={{ fontSize: 10, color: theme.textMuted, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 8 }}>Help</div>
        <button
          onClick={onShowWelcome}
          style={{
            background: 'none', border: 'none', color: theme.accent,
            fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline',
            fontFamily: "'Nunito Sans', sans-serif",
          }}
        >
          View getting started guide
        </button>
      </div>
    </div>
  )
}